import React, { useState } from "react";
import {
  Typography,
  Chip,
  Box,
  Popover,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Divider,
  Stack,
  Tooltip,
} from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import { useAllAccountsViewModel } from "./AllAccountsViewModel";
import AddClient from "../AddClient/AddClient";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import BusinessIcon from "@mui/icons-material/Business";
import CircleIcon from "@mui/icons-material/Circle";
import FactoryIcon from "@mui/icons-material/Factory";
import PublicIcon from "@mui/icons-material/Public";
import { useRbac } from "../../../hooks/RbacContext";
import ContactsIcon from "@mui/icons-material/Contacts";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const POCPopover = ({ anchorEl, selectedPOC, handlePOCClose }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={handlePOCClose}
    anchorOrigin={{
      vertical: "center",
      horizontal: "left",
    }}
    transformOrigin={{
      vertical: "center",
      horizontal: "right",
    }}
    PaperProps={{
      elevation: 3,
      sx: {
        width: 260,
        maxHeight: 400,
        overflow: "hidden",
        ml: 1,
        borderRadius: 1,
      },
    }}
  >
    <Card sx={{ height: "100%" }}>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "primary.lighter",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <ContactsIcon color="primary" sx={{ fontSize: 14 }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "primary.main" }}
        >
          POC List ({selectedPOC?.length || 0})
        </Typography>
      </Box>
      <Box
        sx={{
          overflowY: "auto",
          maxHeight: 345,
          "&::-webkit-scrollbar": { width: 3 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "action.hover",
            borderRadius: 1.5,
          },
        }}
      >
        {selectedPOC?.length > 0 ? (
          selectedPOC.map((poc, index) => (
            <Box
              key={poc.client_poc_id}
              sx={{
                px: 1.5,
                py: 1.25,
                transition: "background-color 0.15s",
                "&:hover": { bgcolor: "action.hover" },
                borderBottom:
                  index < selectedPOC.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: "0.75rem",
                    bgcolor: (theme) =>
                      `${theme.palette.primary.main}${index % 2 ? "90" : ""}`,
                  }}
                >
                  {poc.poc_name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "0.75rem",
                      }}
                      noWrap
                    >
                      {poc.poc_name}
                    </Typography>
                    {poc.is_active && (
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          bgcolor: "success.main",
                          boxShadow: (theme) =>
                            `0 0 0 1px ${theme.palette.success.lighter}`,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.625rem",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {poc.poc_designation || "No Designation"}
                  </Typography>
                  <Stack spacing={0.25}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <EmailIcon sx={{ fontSize: 12, color: "primary.main" }} />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.625rem",
                          color: "text.secondary",
                          maxWidth: 170,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {poc.poc_email}
                      </Typography>
                    </Box>
                    {poc.poc_mobile && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <PhoneIcon sx={{ fontSize: 12, color: "primary.main" }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.625rem",
                            color: "text.secondary",
                          }}
                        >
                          {poc.poc_mobile}
                        </Typography>
                      </Box>
                    )}
                    {poc.poc_business_unit && poc.poc_business_unit.trim() !== "" && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <BusinessIcon
                          sx={{ fontSize: 12, color: "primary.main" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.625rem",
                            color: "text.secondary",
                          }}
                        >
                          {poc.poc_business_unit}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
            <ContactsIcon sx={{ fontSize: 20, mb: 0.5, opacity: 0.5 }} />
            <Typography variant="caption" sx={{ fontSize: "0.625rem" }}>
              No POCs available
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  </Popover>
);

const AllAccounts = () => {
  const {
    isAddClientOpen,
    selectedClient,
    successModal,
    deleteConfirmation,
    loading,
    error,
    clients,
    industries,
    countries,
    // cities,
    // clientDesignations,
    handleAddClient,
    handleEditClient,
    handleCloseDialog,
    handleSubmit,
    setSuccessModal,
    setDeleteConfirmation,
    handleDeleteClick,
    handleDeleteConfirm,
    getActions,
    getIndustryName,
    getCountryName,
    getCityName,
    fetchCountryCities,
    countryCities,
  } = useAllAccountsViewModel();

  const { showLoading, hideLoading } = useLoading();
  const { checkAccess } = useRbac();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPOC, setSelectedPOC] = useState(null);

  const handlePOCClick = (event, poc) => {
    setAnchorEl(event.currentTarget);
    setSelectedPOC(poc);
  };

  const handlePOCClose = () => {
    setAnchorEl(null);
    setSelectedPOC(null);
  };

  const columns = [
    {
      id: "client_name",
      label: "Client (Client Code)",
      render: (value, row) => {
        const clientName = value || "";
        const clientCode = row?.client_code || "";
        const displayText =
          clientName && clientCode ? `${clientName} | ${clientCode}` : "-";
        return (
          <Tooltip title={displayText} arrow>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontSize: "inherit",
                maxWidth: 200,
                cursor: "pointer",
              }}
            >
              {displayText}
            </Typography>
          </Tooltip>
        );
      },
      searchValue: (value, row) => {
        const clientName = value || "";
        const clientCode = row?.client_code || "";
        return `${clientName} ${clientCode}`.toLowerCase();
      },
    },
    {
      id: "client_industry_id",
      label: "Industry",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getIndustryName(value) || "-"}
        </Typography>
      ),
      searchValue: (value) => getIndustryName(value)?.toLowerCase() || "",
    },
    {
      id: "country_id",
      label: "Country",
      align: "left",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getCountryName(value) || "-"}
        </Typography>
      ),
      searchValue: (value) => getCountryName(value)?.toLowerCase() || "",
    },

    {
      id: "updated_on",
      label: "Updated On",
      align: "left",

      render: (value) => {
        if (!value) return "-";
        const [day, month, year] = value.split("/");
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      id: "is_active",
      label: "Status",
      align: "left",
      render: (value) => (
        <Chip
          label={value ? "Active" : "In Active"}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor: value ? "#E8F5E9" : "#FFEBEE",
            color: value ? "#2E7D32" : "#D32F2F",
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      ),
      searchValue: (value) => (value ? "active" : "inactive"),
    },
    {
      id: "client_pocs",
      label: "Contacts",
      align: "center",
      render: (value, row) => (
        <Tooltip title="View list of Point of Contacts" arrow>
          <span>
            <IconButton
              onClick={(e) => handlePOCClick(e, row?.client_pocs || [])}
              size="small"
              disabled={!row?.client_pocs?.length}
              sx={{
                color: (theme) =>
                  row?.client_pocs?.length
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled,
                "&:hover": {
                  backgroundColor: (theme) =>
                    row?.client_pocs?.length
                      ? theme.palette.primary.lighter
                      : "transparent",
                },
              }}
            >
              <ContactsIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
      searchValue: (value) => null,
    },
  ];

  const fixedColumns = {
    left: [],
    right: ["actions"],
  };

  const columnFilters = [
    {
      id: "client_name",
      type: "select",
      label: "Company Name",
      icon: <BusinessIcon />,
      sendIcon: true,
    },
    {
      id: "client_industry_id",
      type: "select",
      label: "Industry",
      render: (value) => getIndustryName(value),
      icon: <FactoryIcon />,
      sendIcon: true,
    },
    {
      id: "country_id",
      type: "select",
      label: "Country",
      render: (value) => getCountryName(value),
      icon: <PublicIcon />,
      sendIcon: true,
    },
    {
      id: "is_active",
      type: "select",
      label: "Status",
      render: (value) => (value ? "Active" : "In Active"),
      icon: <CircleIcon />,
      sendIcon: true,
    },
  ];

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <div>
      <CustomTable
        columns={columns}
        data={clients}
        loading={loading}
        onAdd={handleAddClient}
        showActions={true}
        showAddButton={checkAccess("client", "add")}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="Clients"
        subtitle="Manage and monitor client records"
        addButtonText="Add Client"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
        Searchholder={"Search by name, code, or keyword"}
      />

      <AddClient
        open={isAddClientOpen}
        onClose={handleCloseDialog}
        initialData={selectedClient}
        onSubmit={handleSubmit}
        industries={industries}
        countries={countries}
        fetchCountryCities={fetchCountryCities}
        countryCities={countryCities}
        onDependencyChange={(field, value, setFieldValue) => {
          if (field === "country_id") {
            setFieldValue("city_name", "");
            if (value) {
              fetchCountryCities(value);
            }
          }
        }}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={() => setSuccessModal(null)}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Client"
          message={`Are you sure you want to delete client "${deleteConfirmation.client.client_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <POCPopover
        anchorEl={anchorEl}
        selectedPOC={selectedPOC}
        handlePOCClose={handlePOCClose}
      />
    </div>
  );
};

export default AllAccounts;
